import {
    ADVANCE_INDICATOR_INPUT_TYPE
} from '@app/_shared/modules/strategy-form/components/advance-indicator-form/advance-indicator-form.enums';
import {
    CANDLE_OPTIONS, PRODUCT_TYPE, REACTIVATE_OPTIONS, AND_OR, ChartType, PRICE_METHOD, IndicatorDisplayType, EXECUTION_STYLE,
} from '@app/_shared/modules/strategy-form/strategy.enums';
import { OrderMode, ALGO_STATUS, FEE_SCHEDULES_STRINGS } from '../../shared.enums';
import { AlgoStrategy } from './strategy.model';
import { WebApiStrategyRunningType, WebApiStrategyRunningTypeMap } from '../../../../utProto/webApiStrategyEnums_pb';
import { SearchSymbolResults } from '../symbol-search';
import { STRATEGY_CREATION_METHOD } from '@app/_helpers';
import { BriefPortfolioModel } from '@app/modules/trading/modules/market-place/providers/market-place/market-place.models';

export interface IndicatorDataI {
    type: ADVANCE_INDICATOR_INPUT_TYPE;
    name: string;
    params: any[];
    candleInterval?: number;
    symbol?: {
        security_id: number;
        market_id: string;
    }
}

export interface IndicatorRowI {
    indicators: IndicatorDataI[];
    symbol?: {
        security_id: number;
        market_id: number;
        symbol_id: string;
    };
    refSymbol?: boolean;
    dynamicSymbol?: any;
}

export interface IndicatorObj {
    indicators: IndicatorLogicI;
    // candleInterval: number;
}

export interface IndicatorLogicI {
    indicatorRow: IndicatorRowI[];
    rowComparator: AND_OR[];
}

export interface IndicatorObjectData{
    IndicatorA?: IndicatorTypeData,
    IndicatorB?: IndicatorTypeData
}

export interface IndicatorTypeData{
    selectedIndicatorStr: string,
    selectedIndicator: string,
    selectedIndicatorData?: {[key: string]: any},
}

export interface IndicatorParamsFormData{
    name: string,
    alias?: string,
    indicatorData?: {[key: string]: any}
}

export interface BaseAlsoParams {
    algoName: string;
    algoNotes?: string;
    minimumCapital: number;
    minimumCapitalOnExpiry?: number;
    minCash?: number;
    minCashOnExpiry?: number;
    public: boolean;
    monthlyFee: number;
    feeSchedule: FEE_SCHEDULES_STRINGS; // TODO: Check here if save string
    minimumMultiplier: number;
    maximumMultiplier: number;
    variableFee: number;
    tags: string[];

    allowedDays?: number[];
    version?: number;
    riskOMeter?: number;
}

export interface ExternalAlgoModelI extends BaseAlsoParams {
    creationMethod: STRATEGY_CREATION_METHOD;
    soFileName?: string;
    market?: string;
    symbol?: string;
    restAPIHost?: string;
    apiID: number;

    startDate: string;
    startTime: string;
    squareOffTime: string;
    startTimestamp?: number;
    closeDate: string;
    closeTime: string;
    closeTimestamp?: number;
    isReduceMultiplierAllowed?: boolean;
    daysToExpiryComparator?: string;
    daysToExpiryCount?: number;
    daysToExpiryType?: string;
    executeDays?: string[];
}

export interface SetupFormResultI extends BaseAlsoParams {
    symbols: {
        security_id: number;
        market_id: string;
    }[];
}

export interface DefineStrategyResultI {
    orderMode: OrderMode;
    isLot: boolean; // Lot/Value
    candleSize: CANDLE_OPTIONS;
    orderQty: number;
    productType: PRODUCT_TYPE;
    chartType: ChartType;
    entryPriceMethod: PRICE_METHOD;
    exitPriceMethod: PRICE_METHOD;
    algoCycles: number;
    rerunAlgo: REACTIVATE_OPTIONS;
    switchManually: boolean;
    startDate: string;
    startTime: string;
    startTimestamp?: number;
    closeDate: string;
    closeTime: string;
    closeTimestamp?: number;
    enablePositionProtection: boolean;
    positionRetakeTime: number | Date;
    positionExitTime: number | Date;
    allowedDays?: number[];
}

export interface EntryFormReturnI extends IndicatorLogicI {
    // entryModTick: number;
    // entryModCount: number;
    // entryTimer: number;
    // entryMarketOrder: string;
    // entryLimitPrice: number;
}

export interface ExitFormReturnI extends IndicatorLogicI {
    isPercent: boolean;
    takeProfit: string;
    stopLoss: string;
    enableTrailingTicks: boolean;
    trailingTicks: string;
    // limitOrder: string;
    // limitPrice: string;
    // marketOrder: string;
    // modCount: string;
    // modTick: string;
    // timer: string;
    // trailingStopLoss: boolean;
    // userPercentValueProfit: boolean;
    // userPercentValueStopLoss: boolean;
}

export interface AdvanceUserInputs {
    type: ADVANCE_INDICATOR_INPUT_TYPE;
    value: string;
    symbol?: SearchSymbolResults;
};

export interface SaveAlgoResponse {
    algo_id: string;
}

export interface GetAlgoResponse {
    _id: {
        $oid: string
    };
    algoName: string;
    algoNotes: string;
    client_id: string;
    createdAt: number;
    apiID: number;
    creationMethod: STRATEGY_CREATION_METHOD;
    feeSchedule: FEE_SCHEDULES_STRINGS;
    maximumMultiplier: number;
    minimumCapital: number;
    minimumMultiplier: number;
    monthlyFee: number;
    n_grams: string;
    public: boolean;
    subscribers: string[];
    tags: string[];
    variableFee: number;
    algo: AlgoStrategy;
    deployers: string[];
}

export interface AlgoIdMapping {
    admin_token_id: number;
    algo_id: string;
    strategy_id: number;
    client_id: string;
    market_id: string;
    security_id: number;
    algoState: ALGO_STATUS;
    updated_at: number;
}

/**
 * Algo List Response contains two fields.
 *
 * algos     -> Actual List of saved algos
 * algo_ids  -> List of strategyIDs and admin TokenIDs. They are saved in a different
 *              table than algo itself.
 *
 * @export
 * @interface AlgoListResponse
 */
export interface AlgoListResponse {
    algos: GetAlgoResponse[];
    algo_ids: AlgoIdMapping[];
}

export interface ExecuteAlgoRequest {
    // TODO: remove id
    algo_id?: string;
    state: number;
    algo_type?: WebApiStrategyRunningTypeMap | number;
    basket_id?: number;
    backTestData?: BackTestInterface;
    symbols?: {
        security_id: number;
        market_id: string;
    }[];
    algo_obj?: AlgoStrategy;

    // Below Fields needed in case SO Strategy
    is_external_strategy?: boolean;
    api_id?: number;
    multiplier?: number;
}


export interface BackTestInterface {
    AlgoName?: String;
    initialCapital: number;
    orderQty: number;
    start: number;
    startDate: Date;
    startTime: Date;
    btStartTime: number;
    end: number;
    endDate: Date;
    endTime: Date;
    chartType: string;
    candleSize: string;
    stopLoss: number;
    takeProfit: number;
    symbols: {
        security_id: number;
        market_id: string;
        token_id?: number;
    }[];
    symbol: string[];
    status: number;
    Status: number;

    // needed in case of backtest, to identify when backtest update comes via socket (to show and hide loading row)
    // , Indicator based strategy  backtest run's on token ID which is passed per symbol. External strategy has no
    //  symbols defined so no token id is sent from FE therefore token_id is generated on web server level,
    // so need to send seperatly for external backtest.
    token_id?: number;
}

export interface ExecuteAlgoResponse {
    message: string;
    algo_id: string;
    client_id: string;
    admin_token_id: number;
    security_id: number;
    market_id: string;
    updated_at: number;
}

export interface IndicatorParamI {
    description: string;
    displayType: IndicatorDisplayType;
    defaultValue: any;
    name: string;
    displayName: string;
    possibleValues?: string[];
    valueUsed?: string[];
    type?: string;
    max_value?: number;
    min_value?: number;
}

export interface SupportedIndicatorI {
    description: string;
    group: string;
    isCandleIntervalRequired: boolean;
    isPreviousCandleParamsRequired: boolean;
    isValueComparisonRequired: boolean;
    isScripNameRequired: boolean;
    name: string;
    numberOfInputParams: number;
    numberOfOptionalParams: number;
    numberOfOutputParams: number;
    inputParamList?: IndicatorParamI[];
    optionalParamList?: IndicatorParamI[];
    alias?: string;
    outputParamList: {
        description: string,
        displayType: IndicatorDisplayType,
        flag: string,
        name: string,
        type: string
    }[];
}


export interface StrategySocketUpdateI {
    basketId: number;
    clientId: string;
    portfolioId: string;
    message: string;
    strategyRunningType: number;
    webApiStrategyResponseType: number;
    tokenId: number;
    algoState: ALGO_STATUS;
    old_token_id: number;
    strategy_id: number;
    terminatedAt: number;
    updated_at: number;
    webApiTransactionType: number;
    algo?: string;
    uniqueAlgoName: string
    TerminationTime?: number;
    strategyCustomComment?: string;
    TerminationMessage?: string;
    algoName: string;
    portfolioName: string;
    strategyName?: string;
    positionClosed?: boolean;
    messageDesc?: string;
    strategyTerminationType?: number;
    childStrategyId?: string;
    isAutomatedSquareOff?: string;
    type? : string;
    count?: number;
    triggerType?: number;
    timeStamp?: number;
    targetProfit?: number;
    stopLoss?: number;
    lockProfitAt?: number;
    trailingLockProfit?: number;
    trailingStopLoss?: number;
    multiplier?: number;
}

export interface NotificationResponseFirstFetch {
    clientId: string;
    isRead: boolean;
    msgType: number;
    responseMap: StrategySocketUpdateI;
}

export interface StrategyCRUDSocketMessage {
    message: string;
    algo_id: string;
    algo_body?: AlgoStrategy;
    update: 'put' | 'post' | 'delete';
    subscribed?: boolean;
    bookmarked?: boolean;
    algo_name?: string;
    unique_algo_name?: string;
}

export interface CacheEmitter {
    portfolio: BriefPortfolioModel;
    updatePortfolios: boolean;
    status?: number; // for status update
    type?: string;
}

export interface Conditions {
    condition: string;
    priceCalculationParams: {
        depth: number;
        marketOrderBaseType: number;
        marketOrderCounter: number;
        marketOrderPer: number;
        modifyCnt: number;
        orderType: number;
        orderValidity: number;
        priceMethod: number;
        stopLoss: number;
        stopPriceType: number;
        targetPriceType: number;
        targetProfit: number;
        timer: number;
        topUp: number;
        trailingStoploss: number;
    }
}
export interface StrategyParams {
    algoName: string;
    candleSize: number;
    chartType: string;
    endTime: number;
    entryCondition: Conditions;
    entryIndicatorRow: IndicatorLogicI;
    entryIndicatorString: string;
    executionStyle: string;
    exitCondition: Conditions;
    exitIndicatorRow: IndicatorLogicI;
    exitIndicatorString: string;
    initialCapital: number;
    orderMode: number;
    orderQtyValue: number;
    productType: string;
    qtyType: number;
    reactivateInterval: number;
    scripName: string;
    symbol: {
        security_id: number;
        market_id: string;
    };
    symbolObj?: SearchSymbolResults
    startTime: number;
    totalQtyValue: number;
    valueConvertedLot: number[];
    enablePositionProtection: boolean;
    positionExitTime: number | Date;
    positionRetakeTime: number | Date;
    algoCycles?: number

    isPercent: boolean;
    isStarred: boolean;
    stopLoss: string;
    strategyMode: string;
}

export interface PreviousPositionsInterface {
    unique_algo_name: string;
    order_mode: number;
    filled_quantity: number;
    filled_price: number;
}

export interface ActiveTerminatedAlgo {
    AlgoName: string;
    BacktestResult?: any;
    ClientID: string;
    EndTime: number;
    OrderExecutionType: number;
    PlatformType: number;
    PreviousPositions: PreviousPositionsInterface[];
    SequenceNumber: number;
    StartTime: number;
    StrategyID: number;
    StrategyParams: StrategyParams;
    StrategyRunningType: string;
    StrategyStatus: number;
    StrategyType: number;
    TerminationCode: number;
    TerminationReasonText: string;
    TerminationTime: number;
    Timestamp: number;
    TokenID: number;
    UniqueAlgoName: string;
    Version: number;
    _id: {
        $oid: string
    };
}

export interface SquareoffSocketUpdate {
    clientId: string;
    message: string;
    messageDesc: string;
    responseId: string;
    portfolioId: string;
    strategyId: string;
    tokenId: number;
    webApiStrategyResponseType: number;
    isAutomatedSquareOff?: boolean;
    automatedSquareOffMsg?: string;
    timeStamp?: number;
}

export interface InfraUpdate {
    HFT?: boolean;
    BT_HFT?: boolean;
    DMS_ZMQ?: boolean;
    DMS_HFT?: boolean;
    HFT_HFT_FIX_CONNECTION?: boolean;

    zerodha?: boolean;
    dhan?: boolean;
    shareIndia?: boolean;
    angel_one?: boolean;
    groww?: boolean;
    fyers?: boolean;
    kotak?: boolean;
    default?: boolean;
    paper_trade?: boolean;
    hft_fix_connection?: boolean;
}

export function isInfraReadyForPortfolio(infra: InfraUpdate, runningType: number): boolean {
    return runningType === WebApiStrategyRunningType.WEBAPISTRATEGYRUNNINGTYPE_PAPERTRADE
        ? !!infra?.paper_trade && !!infra?.hft_fix_connection
        : !!infra?.default && !!infra?.hft_fix_connection;
}